import classNames from 'classnames'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import styles from './index.module.scss'
import aboutStyles from '../About/index.module.scss'
import ProviderSectionContainer, {
  PROVIDER_SECTION_IDS,
} from 'app/ProviderDetails/ProviderSectionContainer'
import { isServer } from 'utils/environment'

const useIsomorphicLayoutEffect = !isServer() ? useLayoutEffect : useEffect

type QnASectionProps = {
  providerDetails: Provider
}

const QnASection: React.FC<QnASectionProps> = ({ providerDetails }) => {
  const [showingAll, setShowingAll] = useState<boolean>(false)
  const { faq, firstName } = providerDetails
  const [initialHeight, setInitialHeight] = useState<number>(0)
  const [fullHeight, setFullHeight] = useState<number>(0)

  const faqsRef = useRef<HTMLDivElement>(null)
  const showingLimit = 5
  const bottomMargin = 21

  useIsomorphicLayoutEffect(() => {
    const faqs = faqsRef.current?.children ?? []
    let initialHeight = 0

    for (let i = 0; i < faqs.length; i++) {
      if (i < showingLimit) {
        initialHeight += (faqs[i] as HTMLDivElement).offsetHeight + bottomMargin
        continue
      }

      break
    }

    if (faq.length < 5) {
      setShowingAll(true)
    }

    // Matches Figma (385px measured -> 358px in the comp) on both mobile and desktop.
    setInitialHeight(initialHeight - 27)
    setFullHeight(faqsRef.current?.scrollHeight ?? 0)
  }, [])

  // Transitioning height to/from 'auto' doesn't animate in CSS, so we always
  // animate between two measured pixel values (same approach as About's read-more).
  const qnaSectionStyles = {
    height: showingAll ? `${fullHeight}px` : `${initialHeight}px`,
  }

  if (!faq?.length) {
    return null
  }

  return (
    <ProviderSectionContainer
      heading={`Get to know ${firstName}`}
      id={PROVIDER_SECTION_IDS.QNA}
    >
      <div
        className={classNames(styles.QnaSection, {
          [styles.QnaSection_showAll]: showingAll,
          [styles.QnaSection_showLess]: !showingAll,
        })}
        ref={faqsRef}
        style={qnaSectionStyles}
      >
        {faq.map((qna) => (
          <div key={qna.question}>
            <div
              role="heading"
              aria-level={3}
              className={classNames(
                styles.QnaItem,
                '~font-gibson ~font-medium ~text-black ~text-base'
              )}
              dangerouslySetInnerHTML={{ __html: qna.question }}
            />
            <div
              className="~font-gibson ~font-normal ~text-black ~text-base"
              dangerouslySetInnerHTML={{ __html: qna.answer }}
            />
          </div>
        ))}
      </div>
      {faq?.length > showingLimit && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setShowingAll(!showingAll)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setShowingAll(!showingAll)
            }
          }}
          className={classNames(aboutStyles.readMoreWrapper, {
            [aboutStyles.shade]: !showingAll,
            [styles.readMoreWrapper_open]: showingAll,
          })}
        >
          {showingAll ? (
            <div className={aboutStyles.showHideButton}>
              Read less
              <div
                className={classNames(aboutStyles.arrow, aboutStyles.arrow_up)}
              />
            </div>
          ) : (
            <div className={aboutStyles.showHideButton}>
              Read more
              <div
                className={classNames(
                  aboutStyles.arrow,
                  aboutStyles.arrow_down
                )}
              />
            </div>
          )}
        </div>
      )}
    </ProviderSectionContainer>
  )
}

export default QnASection
